import type { QueryClient } from '@tanstack/react-query';
import { PROPAGATION_LAG_MS } from '@vnetwork/margarita-shell/iam';
import { onKeys } from '@vnetwork/margarita-shell/opennebula/_shared';

/** `['on', <service>, 'instances']` is one workspace's instance list; longer keys under it are details, statuses and tabs. */
export function isInstanceListKey(key: readonly unknown[]): boolean {
  const list = onKeys.instances('');
  return key.length === list.length && key[0] === list[0] && key[2] === list[2];
}

/**
 * Refetches the instance lists on screen after a revoke was detected: the list was read while the person still had the
 * project, so it keeps showing that project's machines until something asks again. One request per list, joining one already
 * in flight; a list nobody shows is only marked stale. Returns the keys it asked for.
 */
export function refetchInstanceLists(qc: QueryClient): readonly (readonly unknown[])[] {
  const keys = qc.getQueryCache().findAll({ queryKey: onKeys.all, predicate: (q) => isInstanceListKey(q.queryKey) }).map((q) => q.queryKey);
  void qc.invalidateQueries({ queryKey: onKeys.all, predicate: (q) => isInstanceListKey(q.queryKey) }, { cancelRefetch: false });
  return keys;
}

/**
 * The second refetch after a revoke. Compute caches its decision for the lag (`PROPAGATION_LAG_MS`) per token, so the refetch
 * made when the revoke is detected can still return the revoked project's machines; this one asks again once the cache has
 * expired. One-shot: a second `schedule` while one is pending changes nothing (one revoke, two refetches), and `cancel` drops
 * it (unmount, or the person chose a project again). No polling.
 */
export function createLaggedRefetch(qc: QueryClient, delayMs = PROPAGATION_LAG_MS) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    if (timer === undefined) return;
    clearTimeout(timer);
    timer = undefined;
  };
  const schedule = () => {
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      refetchInstanceLists(qc);
    }, delayMs);
  };
  return { schedule, cancel };
}
